import {
  TRecord,
  TList,
  TText,
  TTypeConstructor,
  TTypeReference,
  TTypeApplication,
  type Expression,
  EText,
  ERecord,
  EList,
  EUnion,
  EBoolean,
} from '@packages/systems/wfdl-ast';
import {
  pluginName,
  qualify,
  typeName,
  type Qualified,
  type TypeName,
} from '@packages/systems/wfdl-Qualified';
import {
  withSystem,
  type Type,
  Types,
  TypeImplementation,
  Atom,
  type AtomConfig,
  type AtomConfigShape,
} from '@packages/systems/wfdl';
import {toBooleanTag} from '@packages/systems/plugins-utils/Boolean';

export const extraAtomFields: Type = TTypeReference('extraAtomFields');

export const idTextInputType: Type = withSystem(
  {
    label: 'ID',
    defaultValue: '',
    isBindable: true,
    private: true,
    isLocalizable: true,
  },
  TTypeConstructor(qualify(pluginName('Basic'), typeName('IdTextInput')))
);

export const customAttributesType: Type = withSystem(
  {
    label: 'Custom Attribute',
    defaultValue: [],
    isLocalizable: true,
    isBindable: false,
    private: true,
  },
  TList(
    TTypeConstructor(qualify(pluginName('Basic'), typeName('CustomAttribute')))
  )
);

export const styleBlockIds: Type = withSystem(
  {label: 'Style Block Ids', isBindable: false, private: true},
  TList(TText)
);

export const visibilityConditionsType: Type = withSystem(
  {
    label: 'Visibility',
    isBindable: true,
    private: true,
    defaultValue: [],
    isLocalizable: true,
  },
  TTypeConstructor(
    qualify(pluginName('Visibility'), typeName('VisibilityConditions'))
  )
);

export const keepInHtmlType: Type = withSystem(
  {
    isBindable: false,
    private: true,
    defaultValue: toBooleanTag(false),
    label: 'Keep in HTML when hidden',
    isLocalizable: true,
  },
  TTypeConstructor(qualify(pluginName('Boolean'), typeName('Checkbox')))
);

export const excludeSearchType: Type = withSystem(
  {isBindable: false, defaultValue: false, private: true, isLocalizable: true},
  TTypeConstructor(
    qualify(pluginName('Search'), typeName('SearchIndexBoolean'))
  )
);

export const displayNameType: Type = withSystem(
  {label: 'Label', defaultValue: '', isBindable: false, private: true},
  TTypeConstructor(qualify(pluginName('Basic'), typeName('DisplayName')))
);

export const descriptionType: Type = withSystem(
  {label: 'Description', defaultValue: '', isBindable: false, private: true},
  TText
);

export const integratedVisibilityType: Type = withSystem(
  {private: true},
  TRecord({
    conditions: visibilityConditionsType,
    keepInHtml: keepInHtmlType,
  })
);

export type IntegratedTypesType = {
  displayName: Type;
  styleBlockIds: Type;
  xattr: Type;
  attr: Type;
  search: Type;
  visibility: Type;
};

export type IntegratedFieldValuesType = {
  [K in keyof IntegratedTypesType]: Expression;
};

export const defaultIntegratedFieldValues: IntegratedFieldValuesType = {
  displayName: EText(''),
  attr: ERecord({id: EText('')}),
  xattr: EList([]),
  styleBlockIds: EList([]),
  search: ERecord({exclude: EBoolean(false)}),
  visibility: ERecord({
    conditions: EList([]),
    keepInHtml: EUnion({tag: 'False', value: ERecord({})}),
  }),
};

export const defaultIntegratedTypes: IntegratedTypesType = {
  displayName: displayNameType,
  attr: withSystem(
    {private: true},
    TRecord({
      id: idTextInputType,
    })
  ),
  xattr: customAttributesType,
  styleBlockIds,
  search: withSystem(
    {private: false},
    TRecord({
      exclude: excludeSearchType,
    })
  ),
  visibility: integratedVisibilityType,
  // Below are examples of how other integrations may be implemented:
  // this could be replaced by builtinDOM's `attributes` dataType`
  // xattr: withSystem(
  //   {label: 'Custom attributes', isBindable: true},
  //   TList(TRecord({name: TText, value: TText}))
  // ),
};
type IntegratedRootAliasType = TypeImplementation.CommonTypeFields & {
  fields: {
    [key: string]: Type;
  };
};

/**
 * This creates an alias with a type that is integrated with default dataTypes.
 * Useful for creating an alias type to be used as a top level dataType. eg:
 * const config = Atom.create({
 *  showEmpty: true,
 *  render: atomRender,
 *  dataType: createIntegratedRootAlias({
 *    render: aliasRender,
 *    fields: {children: TListOfElements},
 *  }),
});
 */
export const createIntegratedRootAlias = ({
  render,
  fields,
  ...rest
}: IntegratedRootAliasType): TypeImplementation.TypeImplementation =>
  TypeImplementation.Alias({
    ...rest,
    render,
    type: TRecord(
      Types.deepMergeTypes(defaultIntegratedTypes, fields),
      extraAtomFields
    ),
  });

/**
 * Makes it easier to reference integrated root alias types.
 *
 * Each integrated root alias type instance requires an alias param and
 * this util creates one automatically.
 */
export const createIntegratedRootAliasInstance = (
  type: Qualified<TypeName>
): Type =>
  TTypeApplication({
    con: TTypeConstructor(type),
    arg: TTypeReference('T'),
  });

/**
 * create produces an atom configuration with a dataType field that is
 * integrated with the default integrated types. The typical use case for this
 * is to introduce a new atom with horizontal settings dataTypes.
 *
 * Note: The incoming data type field is prioritized over the default integrated field
 * if there is any overlap.
 */
export const createIntegratedAtom = ({
  fields,
  ...rest
}: AtomConfigShape): AtomConfig => {
  const integratedTypes = Types.deepMergeTypes(defaultIntegratedTypes, fields);
  const dataType = TRecord(integratedTypes, extraAtomFields);
  return Atom.create({
    ...rest,
    dataType,
  });
};

export const runtimeProps: Type = withSystem(
  {
    label: 'Runtime Props',
    isBindable: true,
    private: true,
    defaultValue: {},
  },
  TTypeConstructor(qualify(pluginName('Devlink'), typeName('RuntimeProps')))
);

export const slot: Type = withSystem(
  {
    label: 'Slot',
    isBindable: true,
    private: true,
  },
  TTypeConstructor(qualify(pluginName('Devlink'), typeName('Slot')))
);

export const devlinkDataType: Type = withSystem(
  {private: true},
  TRecord({runtimeProps, slot})
);

/**
 * Wraps `createIntegratedAtom` and adds a devlink field to the resulting atom config
 */
export const createDevlinkAtom = (config: AtomConfigShape): AtomConfig => {
  return createIntegratedAtom({
    ...config,
    fields: {
      ...config.fields,
      devlink: devlinkDataType,
    },
  });
};

/**
 * Wraps `createIntegratedRootAlias` and adds a devlink field to the resulting alias config
 */
export const createDevLinkRootAlias = ({
  fields,
  ...rest
}: IntegratedRootAliasType): TypeImplementation.TypeImplementation =>
  createIntegratedRootAlias({
    ...rest,
    fields: {
      ...fields,
      devlink: devlinkDataType,
    },
  });
